import { Card } from '@payaca/ambrosia';
import { FC, useEffect, useMemo, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import CheckboxField from '@payaca/components/checkboxField/CheckboxField';
import CustomerEmailControl from '../customerEmailControl/CustomerEmailControl';

import { Customer } from '@payaca/types/customerTypes';
import { JobContent } from '@payaca/types/jobContentTypes';
import { JobPayment } from '@payaca/types/jobPaymentTypes';
import { Job } from '@payaca/types/jobTypesV2';

import { getSpecificJobTypeText } from '@/helpers/jobHelper';
import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import {
  isEstimate,
  isInvoice,
  isQuote,
} from '@payaca/helpers/jobStatusHelper';

import { actions as jobActions } from '@/api/jobs';
import * as accountActions from '@payaca/store/account/accountActions';

import { useGetProposalEmailTemplate } from '@/api/queries/proposal/useGetProposal';
import { useSelector } from '@/api/state';
import { useUserHasPermission } from '@/hooks/usePermissions';
import CustomerEmailPreview from '@/ui/components/sendJob/CustomerEmailPreview';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';

import TruncatedShadowDomRenderer from '@payaca/components/shadowDomRenderer/TruncatedShadowDomRenderer';
import { buildEmailSubjectPrefixForProjectFromProject } from '@payaca/helpers/dealHelper';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { useDeal } from '@payaca/store/hooks/appState';
import './SendJob.sass';

type Props = {
  job: Job;
  jobContent: JobContent;
  jobPayments: JobPayment[];
  customer: Customer;
  preButtonEmailText: string;
  postButtonEmailText: string;
  sendMeACopy: boolean;
  onUpdate: (value?: { [key: string]: any }) => void;
  onSetIsSendJobDisabled: (isSendJobDisabled: boolean) => void;
};
const SendJob: FC<Props> = ({
  job,
  jobContent,
  jobPayments,
  customer,
  preButtonEmailText,
  postButtonEmailText,
  sendMeACopy,
  onUpdate,
  onSetIsSendJobDisabled,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const deal = useDeal(job.dealId);

  const {
    emailTemplate: proposalHTMLEmailTemplate,
    isLoading: isLoadingProposalHTMlEmailTemplate,
  } = useGetProposalEmailTemplate(job.id.toString());

  const canGetEmailTemplates = useUserHasPermission({
    permissions: [AccountsPermissions.GET_EMAIL_TEMPLATES],
  });

  const customerContactEmail = getJobContactFromCustomer(
    customer,
    job.contactId
  )?.emailAddress;

  const currentAccount = useSelector(
    (state: any) => state.users?.myProfile?.accounts[0]
  );

  useEffect(() => {
    dispatch(jobActions.storeSendInvoiceToCustomerError(null));
    dispatch(accountActions.requestGetEmailTemplates(currentAccount.id, true));

    return () => {
      dispatch(accountActions.clearEmailTemplates());
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'currentAccount.id', and 'dispatch'
  }, []);

  const emailTemplates = useSelector((state) => state.account.emailTemplates);

  const companyName = useSelector(
    (state: any) => state.users.myProfile.accounts[0].companyName
  );

  const emailTemplate = useMemo(() => {
    const jobStatus = job.status;
    if (emailTemplates) {
      if (isQuote(jobStatus)) {
        return emailTemplates.sendQuote;
      } else if (isEstimate(jobStatus)) {
        return emailTemplates.sendEstimate;
      } else if (isInvoice(jobStatus)) {
        return emailTemplates.sendInvoice;
      }
    }
    return '';
  }, [job, emailTemplates]);

  useEffect(() => {
    // ensure everything has loaded before enabling the "Send" button
    if (
      !!customerContactEmail &&
      typeof postButtonEmailText === 'string' &&
      typeof preButtonEmailText === 'string'
    ) {
      return onSetIsSendJobDisabled(false);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'onSetIsSendJobDisabled'
  }, [postButtonEmailText, preButtonEmailText, customerContactEmail]);

  return (
    <div className="send-job">
      <div className="addressee-and-subject-container mt-4">
        <div className="addressee-container">
          {customer && (
            <>
              <div>
                <strong>To:</strong>
              </div>
              {job?.bouncedAt ? (
                <CustomerEmailControl
                  blockedEmails={[]}
                  customer={customer}
                  onPersistCustomer={(customerId: number) => {
                    onSetIsSendJobDisabled(false);
                  }}
                  contactId={job.contactId}
                />
              ) : (
                <span>{customerContactEmail}</span>
              )}
            </>
          )}
        </div>
        <div className="subject-container">
          <div>
            <strong>Subject:</strong>
          </div>
          <div>
            {deal && buildEmailSubjectPrefixForProjectFromProject(deal)}
            Your {getSpecificJobTypeText(job.status)} from {companyName}
          </div>
        </div>
      </div>
      <div className="email-options-container mb-4">
        <CheckboxField
          name="sendMeACopy"
          label="Send me a copy"
          onChange={onUpdate}
          value={sendMeACopy}
        />
      </div>

      {proposalHTMLEmailTemplate && canGetEmailTemplates ? (
        <Card className="overflow-hidden">
          <Card.Body>
            <TruncatedShadowDomRenderer
              html={proposalHTMLEmailTemplate.preview}
            />
          </Card.Body>
        </Card>
      ) : isLoadingProposalHTMlEmailTemplate ? (
        <>Loading...</>
      ) : (
        <ContentPanel className="email-content-container">
          <CustomerEmailPreview
            emailTemplate={emailTemplate}
            job={job}
            jobContent={jobContent}
            jobPayments={jobPayments}
            customer={customer}
            preButtonEmailText={preButtonEmailText}
            postButtonEmailText={postButtonEmailText}
            onChange={onUpdate}
          />
        </ContentPanel>
      )}
    </div>
  );
};

export default SendJob;
